import { useEffect, useRef } from "react"
import { toast, ToastOptions } from "react-toastify"
import { AlertTriangle } from "lucide-react"

import AlertTriangleIcon from "@/assets/icons/alertTriangle/medium.svg?react"
import CheckCircleIcon from "@/assets/icons/checkCircle/filled/medium.svg?react"
import CloseIcon from "@/assets/icons/close/bold.svg?react"
import InfoIcon from "@/assets/icons/info/filled/bold.svg?react"
import { cn } from "@/lib/utils"

/**
 * `light` and `errorBold` are the design system's toast «Bold, with action» (MPE-1444): `light` is the unknown save
 * result's look (S8u), `errorBold` the failed save's (S8). The other four are the original toasts, unchanged.
 */
type ToastType = "success" | "error" | "info" | "warning" | "light" | "errorBold"

/** A toast's secondary action: a real button with its label as its name; using it also closes the toast. */
export interface ToastAction {
  label: string
  onClick: () => void
}

interface ToastContentProps {
  title?: string
  message: string
  /** `light` and `errorBold` only. With an action, the toast stays until the action or the close is used. */
  action?: ToastAction
  /** `light` and `errorBold` only: focus moves to the message when the toast shows (a submit error, REQ-A11Y-08). */
  focusOnShow?: boolean
  closeToast?: () => void
}

/** `errorBold` always shows a close button, so its accessible name is required; no other type has one. */
type CloseLabelProps =
  | { type: "errorBold"; closeLabel: string }
  | { type: Exclude<ToastType, "errorBold">; closeLabel?: never }

type CustomToastProps = ToastContentProps &
  CloseLabelProps & {
    className?: string
    options?: Partial<ToastOptions>
  }

type ActionToastType = Extract<ToastType, "light" | "errorBold">

type ActionToastProps = ToastContentProps & CloseLabelProps & { type: ActionToastType }

type ClassicToastType = Exclude<ToastType, ActionToastType>

const isActionToastType = (type: ToastType): type is ActionToastType => type === "light" || type === "errorBold"

const toastConfig: Record<
  ClassicToastType,
  {
    icon: JSX.Element
    containerClasses: string
    color: string
    secondaryBG: string
  }
> = {
  success: {
    icon: <CheckCircleIcon className="h-5 w-5" />,
    containerClasses: "border-2 border-safe-secondBg bg-safe-mainBg",
    color: "text-safe-primary",
    secondaryBG: "bg-safe-secondBg",
  },
  error: {
    icon: <AlertTriangle className="h-5 w-5" />,
    containerClasses: "border-2 border-danger-secondBg bg-danger-mainBg",
    color: "text-danger-primary",
    secondaryBG: "bg-danger-secondBg",
  },
  info: {
    icon: <InfoIcon className="h-5 w-5" />,
    containerClasses: "border-2 border-sea-secondBg bg-sea-mainBg",
    color: "text-sea-primary",
    secondaryBG: "bg-sea-secondBg",
  },
  warning: {
    icon: <AlertTriangle className="h-5 w-5" />,
    containerClasses: "border-2 border-solid border-warning-secondBg bg-warning-mainBg",
    color: "text-warning-primary",
    secondaryBG: "bg-warning-secondBg",
  },
}

const CustomToast: React.FC<ToastContentProps & { type: ClassicToastType; className?: string }> = ({
  type,
  title,
  message,
  className,
  closeToast,
}) => {
  const config = toastConfig[type]
  return (
    <div
      className={cn(
        "flex w-full flex-row items-center justify-between rounded-md p-2 py-2",
        config.containerClasses,
        className,
      )}
      role="alert"
      aria-live="polite"
    >
      <div className={cn("mr-4 rounded-sm p-[3px]", config.color, config.secondaryBG)}>{config.icon}</div>
      <div className="flex w-full flex-col">
        {title && <div className={cn("mb-1 text-base font-bold leading-5", config.color)}>{title}</div>}
        <div className={cn("text-base leading-5", config.color)}>{message}</div>
      </div>
      <CloseIcon className={cn("h-4 w-4 cursor-pointer", config.color)} onClick={closeToast} />
    </div>
  )
}

/**
 * Figma file pI2ub09PAm7L2ELDVZsjob, section 34319:41175, the toast «Bold, with action»: 12 / 8 px padding, 8 px
 * gaps and radius, title Body 1 Medium, description Body 2, the action a 32 px-radius pill in Body 2 Medium.
 * Longer copy (Spanish) widens the toast from Figma's width up to 600 px, never wider than the window less 48 px a
 * side: the title stays on one line, and only then does the description wrap, in balanced lines (Inge, 3 Nov). Only
 * when even the title can't fit (a very narrow or zoomed window) does it wrap too, never under the action (#581).
 * - `light`: S8u 34967:130574 (MPE-1444). 520 × 58; two fills of brand/primary at 5 % over white, a 1 px indigo border
 *   at 30 %, a shadow of 3 % · 1 px down; ink text; the action outlined (white, neutral border, indigo text). No icon,
 *   no close, no green or error colour.
 * - `errorBold`: S8 34380:46225. 440 × 58; danger/primary with white text, the warning icon, the action on a dark tint
 *   and a close.
 */
const ACTION_TOAST_LOOK: Record<
  ActionToastType,
  { container: string; title: string; message: string; action: string; focusRing: string }
> = {
  light: {
    // Figma draws the 1 px border inside the 520 × 58 box: 1 px less padding than errorBold's 12 / 8.
    container:
      "min-w-[min(520px,calc(100vw-96px))] border border-indigo-primary/30 px-[11px] py-[7px] bg-neutral-mainBg bg-[linear-gradient(theme(colors.brand.primary/5%),theme(colors.brand.primary/5%)),linear-gradient(theme(colors.brand.primary/5%),theme(colors.brand.primary/5%))] drop-shadow-[0_1px_2px_rgba(22,20,41,0.03)]",
    title: "text-neutral-title",
    // Figma opacity/dark/80 (no token): 8.8:1 on the tinted surface.
    message: "text-[rgba(22,20,41,0.8)]",
    // Figma draws the 1 px border inside the 104 × 36 pill: 1 px less padding than the borderless pill.
    action: "border border-neutral-border bg-neutral-mainBg px-[11px] py-[7px] text-indigo-primary",
    focusRing: "focus-visible:ring-indigo-primary",
  },
  errorBold: {
    container: "min-w-[min(440px,calc(100vw-96px))] bg-danger-primary px-3 py-2",
    title: "text-neutral-mainBg",
    message: "text-neutral-mainBg",
    // Figma opacity/dark/40 (no token) over danger/primary.
    action: "bg-[rgba(22,20,41,0.4)] px-3 py-2 text-neutral-mainBg",
    focusRing: "focus-visible:ring-neutral-mainBg",
  },
}

/** The content of a `light` or `errorBold` toast; react-toastify renders it, `Toast` below shows it. */
export function ActionToast({
  type,
  title,
  message,
  action,
  closeLabel,
  focusOnShow = false,
  closeToast,
}: ActionToastProps) {
  const look = ACTION_TOAST_LOOK[type]
  const text = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (focusOnShow) text.current?.focus()
  }, [focusOnShow])

  return (
    <div
      data-slot="toast"
      data-type={type}
      className={cn(
        // The app font, whatever the toast container sets.
        "flex w-max max-w-[min(600px,calc(100vw-96px))] items-center gap-2 rounded-[8px] text-left font-sans",
        look.container,
      )}
    >
      {type === "errorBold" && (
        <span aria-hidden="true" className="flex size-8 shrink-0 items-center justify-center rounded-[6px]">
          <AlertTriangleIcon className="size-[26.67px] text-neutral-mainBg" />
        </span>
      )}
      <div
        ref={text}
        data-slot="toast-message"
        tabIndex={focusOnShow ? -1 : undefined}
        className="flex min-w-0 flex-1 flex-col leading-[1.4] outline-none"
      >
        {title && <p className={cn("text-balance text-body1 font-medium", look.title)}>{title}</p>}
        <p className={cn("text-balance text-body2", look.message)}>{message}</p>
      </div>
      {action && (
        <button
          type="button"
          onClick={() => {
            action.onClick()
            closeToast?.()
          }}
          className={cn(
            "shrink-0 whitespace-nowrap rounded-[32px] text-body2 font-medium leading-[1.4]",
            "focus-visible:outline-none focus-visible:ring-2",
            look.action,
            look.focusRing,
          )}
        >
          {action.label}
        </button>
      )}
      {type === "errorBold" && (
        <button
          type="button"
          aria-label={closeLabel}
          onClick={closeToast}
          className={cn(
            "flex size-4 shrink-0 items-center justify-center rounded-sm text-neutral-mainBg",
            "focus-visible:outline-none focus-visible:ring-2",
            look.focusRing,
          )}
        >
          <CloseIcon aria-hidden="true" className="size-4" />
        </button>
      )}
    </div>
  )
}

/**
 * react-toastify wraps every toast in a card of its own: white, a shadow, 8 px padding, at least 64 px tall, in its own
 * font. The two looks above are complete without it, so their wrapper has no card, background, padding or shadow, and
 * doesn't clip; `!` because the library's stylesheet may load before ours (the app) or after it (Storybook). A toast
 * wider than its container (the app's is 380 px) extends to the left from the container's right edge.
 */
const ACTION_TOAST_WRAPPER =
  "!m-0 !min-h-0 !overflow-visible !rounded-none !bg-transparent !p-0 !shadow-none !justify-end"
const ACTION_TOAST_BODY = "!m-0 !p-0"

type ToastClassName = ToastOptions["className"]

/** A caller's classes are added to the resets, never in their place (#581). */
function withResets(resets: string, extra: ToastClassName): ToastClassName {
  if (typeof extra === "function") return (context) => cn(resets, extra(context))
  return cn(resets, extra)
}

const defaultOptions: ToastOptions = {
  autoClose: 5000,
  closeOnClick: false,
  pauseOnHover: true,
  draggable: true,
  hideProgressBar: true,
  closeButton: false,
  position: "top-right",
  className: "p-1 m-0 h-fit bg-transparent [&>*]:p-0 [&>*]:m-0 shadow-none",
}

const Toast = (props: CustomToastProps) => {
  const { type, title, message, action, focusOnShow, options = {} } = props
  const withAction = isActionToastType(type)
  const mergedOptions = {
    ...defaultOptions,
    // One live region, the toast body: a status for the calm `light` look, an alert for `errorBold`.
    ...(withAction ? { role: type === "light" ? "status" : "alert" } : {}),
    ...options,
    // After the caller's options (#581): the new looks keep their wrapper resets, and a toast with an action stays
    // until the action or the close is used (WCAG 2.2.1). Toasts without an action keep the caller's choices.
    ...(withAction
      ? {
          className: withResets(ACTION_TOAST_WRAPPER, options.className),
          bodyClassName: withResets(ACTION_TOAST_BODY, options.bodyClassName),
        }
      : {}),
    ...(withAction && action ? { autoClose: false as const } : {}),
    toastId: options.toastId || message, // Deduplicate identical messages
  }

  // Clear the waiting queue for errors so that previous errors don't keep popping up
  if (type === "error" || type === "errorBold") {
    toast.clearWaitingQueue()
  }

  return toast(({ closeToast }) => {
    const content = { title, message, action, focusOnShow, closeToast }
    if (props.type === "errorBold") return <ActionToast {...content} type="errorBold" closeLabel={props.closeLabel} />
    if (props.type === "light") return <ActionToast {...content} type="light" />
    return <CustomToast type={props.type} title={title} message={message} closeToast={closeToast} />
  }, mergedOptions)
}

export default Toast
